/**
 * Geography > PSA Mappings (WPS-222) - which plant service area serves a zone
 * for each business line. The resolution chain is postal code -> zone -> PSA
 * (by business) -> available products.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Route, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { psaMappings, useGeoMeta, type PsaMappingRow, useRefreshGeoData } from '@/admin/hooks/use-geo-reference'

import { GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneOptions, withCurrentValue, zoneLabel } from './geography-utils'
import { saveWithFieldErrors, useListState } from './section-crud'

export function PsaMappingsSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('geo-zones.create')
  const canEdit = can('geo-zones.edit')
  const canDelete = can('geo-zones.delete')

  const list = useListState()

  const refreshGeoData = useRefreshGeoData()
  const { data, isLoading, isFetching, isError, refetch } = psaMappings.useList(list.params)
  const create = psaMappings.useCreate()
  const update = psaMappings.useUpdate()
  const remove = psaMappings.useDelete()
  const { data: meta } = useGeoMeta()
  const zoneOptions = useZoneOptions()
  const plantOptions = useZoneOptions('plant_service_area')

  const [editing, setEditing] = useState<PsaMappingRow | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<PsaMappingRow | null>(null)
  const [showImport, setShowImport] = useState(false)

  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.psa.countOne', 'mapping'),
    t('geography.psa.countMany', 'mappings'),
  )

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'zone', label: t('geography.psa.field.zone', 'Zone'), kind: 'field', options: zoneOptions },
      { key: 'psa', label: t('geography.psa.field.psa', 'Plant service area'), kind: 'field', options: plantOptions },
      {
        key: 'business',
        label: t('geography.psa.field.business', 'Business line'),
        kind: 'field',
        options: meta?.businesses ?? [],
      },
    ],
    [meta?.businesses, plantOptions, t, zoneOptions],
  )

  const businessOptions = useMemo(
    () => withCurrentValue(meta?.businesses, editing?.business),
    [editing?.business, meta?.businesses],
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'zone_uuid',
        display_name: t('geography.psa.field.zone', 'Zone'),
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
        configuration_data: { searchable: true },
      },
      {
        code_name: 'psa_uuid',
        display_name: t('geography.psa.field.psa', 'Plant service area'),
        field_type: 'SELECT',
        required: true,
        options: plantOptions,
        configuration_data: { searchable: true },
      },
      {
        code_name: 'business',
        display_name: t('geography.psa.field.business', 'Business line'),
        // Existing lines are offered; a new line is typed in when none exist yet.
        field_type: businessOptions.length ? 'SELECT' : 'TEXT',
        required: true,
        options: businessOptions,
        description: t(
          'geography.psa.field.businessHint',
          'A zone maps to one plant per business line.',
        ),
      },
    ],
    [businessOptions, plantOptions, t, zoneOptions],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? { zone_uuid: editing.zone_uuid, psa_uuid: editing.psa_uuid, business: editing.business }
        : {},
    [editing],
  )

  const columns = useMemo<DataTableColumn<PsaMappingRow>[]>(
    () => [
      {
        id: 'zone',
        header: t('geography.psa.column.zone', 'Zone'),
        sortable: true,
        sortField: 'zone__name',
        cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} />,
      },
      {
        id: 'business',
        header: t('geography.psa.column.business', 'Business line'),
        sortable: true,
        sortField: 'business',
        cell: (r) => <TextCell muted value={r.business} />,
      },
      {
        id: 'psa',
        header: t('geography.psa.column.psa', 'Plant service area'),
        sortable: true,
        sortField: 'psa__name',
        cell: (r) => <TextCell value={zoneLabel(r.psa_name, r.psa_code)} />,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setEditing(r)
                    setFormOpen(true)
                  }}
                >
                  {t('geography.common.edit', 'Edit')}
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                  {t('geography.common.delete', 'Delete')}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canDelete, canEdit, t],
  )

  return (
    <>
      <GeographySectionShell
        testId="geography-psa"
        title={t('geography.psa.title', 'PSA Mappings')}
        description={t(
          'geography.psa.description',
          'Which plant service area serves each zone, per business line. Registration resolves an address to a zone, then to the plant whose products are available there.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.psa.searchPlaceholder', 'Search zone, plant or business')}
        facets={facets}
        filterValues={list.filterValues}
        onFilterValuesChange={list.setFilterValues}
        actions={
          canCreate ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <Upload className="h-3.5 w-3.5" />
                {t('geography.common.import', 'Import')}
              </Button>
              <Button
                size="sm"
                data-testid="geography-psa-add-btn"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('geography.psa.add', 'Add mapping')}
              </Button>
            </>
          ) : null
        }
      >
        <DataTable<PsaMappingRow>
          tableId="geography-psa-mappings"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState
              icon={Route}
              title={t('geography.psa.emptyTitle', 'No PSA mappings')}
              description={t(
                'geography.psa.emptyDescription',
                'Map each zone to the plant that serves it so registration can filter products by location.',
              )}
            />
          }
        />
      </GeographySectionShell>

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        title={
          editing
            ? t('geography.psa.editTitle', 'Edit PSA mapping')
            : t('geography.psa.addTitle', 'Add PSA mapping')
        }
        fields={fields}
        values={values}
        isPending={create.isPending || update.isPending}
        onSave={(v, _reason, helpers) =>
          saveWithFieldErrors(
            async () => {
              const payload = {
                zone_uuid: String(v.zone_uuid),
                psa_uuid: String(v.psa_uuid),
                business: String(v.business ?? '').trim(),
              }
              if (editing) {
                await update.mutateAsync({ uuid: editing.uuid, data: payload })
              } else {
                await create.mutateAsync(payload)
              }
            },
            helpers,
            editing
              ? t('geography.psa.updated', 'Mapping updated.')
              : t('geography.psa.created', 'Mapping created.'),
            t('geography.psa.saveFailed', 'Could not save the mapping.'),
          )
        }
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={t('geography.psa.deleteTitle', 'Delete PSA mapping?')}
        description={
          deleting
            ? t(
                'geography.psa.deleteDescription',
                '{{zone}} will no longer resolve to {{psa}} for {{business}}.',
                {
                  zone: zoneLabel(deleting.zone_name, deleting.zone_code),
                  psa: deleting.psa_name,
                  business: deleting.business,
                },
              )
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await remove.mutateAsync(deleting.uuid)
            toast.success(t('geography.psa.deleted', 'Mapping deleted.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(err, t('geography.psa.deleteFailed', 'Could not delete the mapping.'))
          }
        }}
      />

      <BulkImportDialog
        onComplete={refreshGeoData}
        open={showImport}
        onOpenChange={setShowImport}
        target="geo_psa_mappings"
        title={t('geography.psa.importTitle', 'Import PSA mappings')}
      />
    </>
  )
}
